import { useState, useEffect, ChangeEvent, FormEvent, useCallback, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Upload, Loader2, Check, X, Mail, Phone } from 'lucide-react';
import { useAuth } from '../../lib/auth';
import { supabase } from '../../lib/supabase';
import StyledLink from '../../components/header-footer/StyledLink';
import logger from '../../utils/logger';
import { useMediaUpload, MEDIA_TYPES } from '../../hooks/useMediaUpload';
import PhoneOTPModal from '../../components/PhoneOTPModal';
import EmailOTPModal from '../../components/EmailOTPModal';
import { LocationPermissionModal } from '../../components/modals/LocationPermissionModal';
import { locationService, LocationData } from '../../services/location-service';
import { useGDPR } from '../../context/GDPRContext';
import toast from 'react-hot-toast';

interface PendingUserData {
  contact: string;
  password: string;
  username: string;
  name: string;
  birthdate: string;
  avatarUrl: string | null;
}

/**
 * Displays a modal dialog welcoming the user after successful signup.
 *
 * Renders a congratulatory message and a "Get Started" button that triggers the provided {@link onClose} callback when clicked. The modal is visible only when {@link isOpen} is true.
 *
 * @param isOpen - Whether the modal is visible.
 * @param onClose - Callback invoked when the user closes the modal.
 */
function SignupSuccessModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-black rounded-xl p-6 max-w-sm w-full mx-4 border-0">
        <h2 className="text-2xl font-bold text-center mb-4 dark:text-white">Welcome to MoZo.Live! 🎉</h2>
        <p className="text-gray-600 dark:text-gray-400 text-center mb-6">
          Congratulations on joining our community! We're excited to have you here.
        </p>
        <button
          onClick={onClose}
          className="w-full py-4 px-5 bg-gray-900 dark:bg-gray-700 text-white rounded-2xl shadow-lg hover:bg-gray-800 dark:hover:bg-gray-600 transition-all duration-300"
        >
          Get Started
        </button>
      </div>
    </div>
  );
}

/**
 * Renders the user signup page with avatar upload, form validation, username availability checking, and optional geolocation capture.
 *
 * Provides a registration form that collects user details, validates input, checks username availability in real time, uploads the avatar to Digital Ocean Spaces, and creates a new user account. Displays a success modal upon successful signup.
 *
 * @remark
 * Geolocation is attempted on mount but is optional; errors in location or address lookup do not block signup. Username availability is checked with a debounce and validated for allowed characters. Users must be at least 13 years old to register.
 */
export default function Signup() {
  const { signUp } = useAuth();
  const { gdprSettings } = useGDPR();
  const location = useLocation();
  const [signupMethod, setSignupMethod] = useState<'email' | 'phone'>('email');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [username, setUsername] = useState('');
  const [month, setMonth] = useState('');
  const [day, setDay] = useState('');
  const [year, setYear] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [avatar, setAvatar] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [isUsernameAvailable, setIsUsernameAvailable] = useState<boolean | null>(null);
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [pendingUserData, setPendingUserData] = useState<PendingUserData | null>(null);
  const [showLocationPermission, setShowLocationPermission] = useState(false);
  const [locationData, setLocationData] = useState<LocationData | null>(null);
  const navigate = useNavigate();

  // Refs for cleanup
  const usernameCheckTimeoutRef = useRef<NodeJS.Timeout>();
  const geolocationAbortRef = useRef<AbortController>();

  // Digital Ocean Spaces upload hook
  const { upload: uploadToSpaces, isUploading: isUploadingAvatar, progress: uploadProgress } = useMediaUpload({
    onError: (error) => setError(`Avatar upload failed: ${error}`),
  });

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const days = Array.from({ length: 31 }, (_, i) => i + 1);
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 100 }, (_, i) => currentYear - i);

  // Get username from URL query parameters if present
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const usernameParam = params.get('username');
    if (usernameParam) {
      setUsername(usernameParam);
    }
  }, [location.search]);

  // Location and geolocation handling
  useEffect(() => {
    // Show location permission dialog on component mount
    const timer = setTimeout(() => {
      if (locationService.isGeolocationSupported()) {
        setShowLocationPermission(true);
      } else {
        // Fallback to IP-based location if geolocation not supported
        handleIPLocation();
      }
    }, 1000); // Small delay for better UX

    return () => {
      clearTimeout(timer);
      if (geolocationAbortRef.current) {
        geolocationAbortRef.current.abort();
      }
    };
  }, []);

  const handleLocationAllow = async () => {
    setShowLocationPermission(false);
    try {
      const location = await locationService.getUserLocation(true);
      if (location) {
        setLocationData(location);
        console.log('Location obtained:', locationService.formatLocation(location));
      } else {
        // Fallback to IP location
        await handleIPLocation();
      }
    } catch (error) {
      console.warn('Location access failed:', error);
      await handleIPLocation();
    }
  };

  const handleLocationDeny = async () => {
    setShowLocationPermission(false);
    // Use IP-based location as fallback
    await handleIPLocation();
  };

  const handleIPLocation = async () => {
    try {
      const location = await locationService.getUserLocation(false);
      if (location) {
        setLocationData(location);
        console.log('IP-based location:', locationService.formatLocation(location));
      }
    } catch (error) {
      console.warn('IP location lookup failed:', error);
    }
  };

  // Cleanup avatar preview URL on unmount or avatar change
  useEffect(() => {
    return () => {
      if (avatarPreview) {
        URL.revokeObjectURL(avatarPreview);
      }
    };
  }, [avatarPreview]);

  // Debounced username availability check
  const checkUsername = useCallback(async () => {
    if (!username || username.length < 3) {
      setIsUsernameAvailable(null);
      return;
    }

    // Validate username format
    const usernameRegex = /^[a-zA-Z0-9._]+$/;
    if (!usernameRegex.test(username)) {
      setIsUsernameAvailable(false);
      return;
    }

    setIsCheckingUsername(true);
    try {
      const { data, error } = await supabase
        .from('users')
        .select('username')
        .eq('username', username)
        .maybeSingle();

      if (error) throw error;
      setIsUsernameAvailable(!data);
    } catch (error) {
      logger.error('Error checking username:', error);
      setIsUsernameAvailable(false);
    } finally {
      setIsCheckingUsername(false);
    }
  }, [username]);

  useEffect(() => {
    // Clear previous timeout
    if (usernameCheckTimeoutRef.current) {
      clearTimeout(usernameCheckTimeoutRef.current);
    }

    // Debounce username check by 500ms
    usernameCheckTimeoutRef.current = setTimeout(() => {
      checkUsername();
    }, 500);

    // Cleanup timeout on unmount
    return () => {
      if (usernameCheckTimeoutRef.current) {
        clearTimeout(usernameCheckTimeoutRef.current);
      }
    };
  }, [checkUsername]);

  // Cleanup geolocation on unmount
  useEffect(() => {
    return () => {
      if (geolocationAbortRef.current) {
        geolocationAbortRef.current.abort();
      }
    };
  }, []);

  const handleAvatarChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Revoke previous preview URL to prevent memory leak
      if (avatarPreview) {
        URL.revokeObjectURL(avatarPreview);
      }
      
      setAvatar(file);
      setAvatarPreview(URL.createObjectURL(file));
    }
  };

  // Validate that all required fields are present
  const validateRequiredFields = () => {
    const contactField = signupMethod === 'email' ? email : phone;
    if (!name || !contactField || !username || !month || !day || !year || !password || !confirmPassword) {
      throw new Error('All fields are required');
    }
  };

  // Validate phone number format
  const validatePhone = () => {
    if (signupMethod === 'phone') {
      const phoneRegex = /^\+?[1-9]\d{1,14}$/; // Basic international phone format
      if (!phoneRegex.test(phone.replace(/\s/g, ''))) {
        throw new Error('Please enter a valid phone number with country code (e.g., +1234567890)');
      }
    }
  };

  // Validate email format
  const validateEmail = () => {
    if (signupMethod === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        throw new Error('Please enter a valid email address');
      }
    }
  };

  // Validate password requirements
  const validatePassword = () => {
    if (password !== confirmPassword) {
      throw new Error('Passwords do not match');
    }

    if (password.length < 8) {
      throw new Error('Password must be at least 8 characters long');
    }
  };

  // Calculate age from birthdate
  const calculateAge = (birthdate: Date, today: Date = new Date()): number => {
    let age = today.getFullYear() - birthdate.getFullYear();
    const m = today.getMonth() - birthdate.getMonth();

    // Adjust age if birthday hasn't occurred yet this year
    if (m < 0 || (m === 0 && today.getDate() < birthdate.getDate())) {
      age--;
    }

    return age;
  };

  // Validate minimum age requirement
  const validateMinimumAge = (minimumAge: number = 13) => {
    const birthdate = new Date(`${month} ${day}, ${year}`);
    const age = calculateAge(birthdate);

    if (age < minimumAge) {
      throw new Error(`You must be at least ${minimumAge} years old to sign up`);
    }
  };

  // Main validation function that calls all validation methods
  const validateForm = () => {
    validateRequiredFields();
    validateEmail();
    validatePhone();
    validatePassword();
    validateMinimumAge();

    return true;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);

      validateForm();

      // Upload avatar to Digital Ocean Spaces
      const birthdate = `${month} ${day}, ${year}`;
      
      const uploadResult = avatar ? await uploadToSpaces(avatar, MEDIA_TYPES.AVATAR, username) : { success: true };

      if (!uploadResult.success) {
        throw new Error(uploadResult.error || 'Failed to upload avatar');
      }

      const avatarUrl = avatar ? uploadResult.url! : null;

      // Store user data for after OTP verification
      setPendingUserData({
        contact: signupMethod === 'email' ? email : phone,
        password,
        username,
        name,
        birthdate,
        avatarUrl
      });

      // Send OTP via Supabase before showing modal
      if (signupMethod === 'phone') {
        const { error } = await supabase.auth.signInWithOtp({
          phone: phone,
          options: {
            shouldCreateUser: false // We'll create the user after OTP verification
          }
        });
        
        if (error) {
          throw new Error('Failed to send SMS verification code. Please try again.');
        }
        
        toast.success('SMS verification code sent to your phone');
      } else {
        const { error } = await supabase.auth.signInWithOtp({
          email: email,
          options: {
            shouldCreateUser: false // We'll create the user after OTP verification
          }
        });
        
        if (error) {
          throw new Error('Failed to send email verification code. Please try again.');
        }
        
        toast.success('Email verification code sent to your inbox');
      }

      // Show OTP modal after successfully sending code
      setShowOTPModal(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to validate form');
    } finally {
      setLoading(false);
    }
  };

  const handleOTPVerify = async (code: string): Promise<boolean> => {
    try {
      setLoading(true);
      
      if (!pendingUserData) {
        throw new Error('No pending signup data found');
      }

      // Validate OTP code format
      if (code.length !== 6 || !/^\d{6}$/.test(code)) {
        throw new Error('Please enter a valid 6-digit code');
      }

      // Verify OTP with Supabase
      if (signupMethod === 'phone') {
        const { error } = await supabase.auth.verifyOtp({
          phone: pendingUserData.contact,
          token: code,
          type: 'sms'
        });
        
        if (error) {
          throw new Error('Invalid verification code. Please try again.');
        }
      } else {
        const { error } = await supabase.auth.verifyOtp({
          email: pendingUserData.contact,
          token: code,
          type: 'email'
        });
        
        if (error) {
          throw new Error('Invalid verification code. Please try again.');
        }
      }

      // OTP verified, proceed with account creation
      await signUp(
        pendingUserData.contact,
        pendingUserData.password,
        pendingUserData.username,
        pendingUserData.name,
        pendingUserData.birthdate,
        pendingUserData.avatarUrl,
        gdprSettings,
        locationData
      );

      setShowOTPModal(false);
      setShowSuccessModal(true);
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Verification failed';
      setError(errorMessage);
      toast.error(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    navigate('/feed');
  };

  const handleUsernameBlur = () => {
    checkUsername();
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-black flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Link
          to="/"
          className="absolute top-4 left-4 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
        >
          <ArrowLeft className="w-6 h-6" />
        </Link>
        <h1 className="text-center text-3xl font-bold text-gray-900 dark:text-white">
          Create your account
        </h1>
        <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
          Already have an account?{' '}
          <StyledLink to="/login">
            Sign in
          </StyledLink>
        </p>

      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white dark:bg-black py-8 px-4 shadow sm:rounded-lg sm:px-10 border-0">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Avatar Upload */}
            <div className="flex flex-col items-center">
              <div className="relative">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarChange}
                  className="hidden"
                  id="avatar-upload"
                />
                <label
                  htmlFor="avatar-upload"
                  className="cursor-pointer block"
                >
                  <div className="w-24 h-24 rounded-full overflow-hidden bg-gray-100 dark:bg-gray-900 hover:opacity-90 transition-opacity">
                    {avatarPreview ? (
                      <img
                        src={avatarPreview}
                        alt="Avatar preview"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Upload className="w-8 h-8 text-gray-400 dark:text-white" />
                      </div>
                    )}
                  </div>
                </label>
              </div>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Upload your avatar (optional)</p>
              {isUploadingAvatar && (
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  Uploading avatar... {uploadProgress ? `${uploadProgress.percentage}%` : '0%'}
                </p>
              )}
            </div>

            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Name
              </label>
              <div className="mt-1 relative bg-white dark:bg-black rounded-2xl shadow-lg overflow-hidden border-0 transition-all duration-300 hover:shadow-xl">
                <input
                  id="name"
                  name="name"
                  type="text"
                  autoComplete="name"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="appearance-none block w-full px-5 py-4 bg-transparent placeholder-gray-400 focus:outline-none focus:ring-0 sm:text-sm dark:text-white"
                  placeholder="Enter your name"
                />
              </div>
            </div>

            {/* Username */}
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Username
              </label>
              <div className="mt-1 relative bg-white dark:bg-black rounded-2xl shadow-lg overflow-hidden border-0 transition-all duration-300 hover:shadow-xl">
                <input
                  id="username"
                  type="text"
                  required
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  onBlur={handleUsernameBlur}
                  className="block w-full px-5 py-4 bg-transparent placeholder-gray-400 focus:outline-none focus:ring-0 sm:text-sm dark:text-white pr-12"
                  placeholder="Choose a username"
                />
                {/* Username availability indicator */}
                {isUsernameAvailable !== null && username.length > 0 && (
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                    {isUsernameAvailable ? (
                      <Check className="h-5 w-5 text-green-500" />
                    ) : (
                      <X className="h-5 w-5 text-red-500" />
                    )}
                  </div>
                )}
                {isCheckingUsername && (
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
                  </div>
                )}
              </div>
              {isUsernameAvailable === false && username.length > 0 && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                  Username is already taken
                </p>
              )}
              {isUsernameAvailable === true && username.length > 0 && (
                <p className="mt-1 text-sm text-green-600 dark:text-green-400">
                  Username is available
                </p>
              )}
            </div>

            {/* Email/Phone Tabs and Input */}
            <div>
              {/* Tab Headers */}
              <div className="flex space-x-1 mb-3">
                <button
                  type="button"
                  onClick={() => setSignupMethod('email')}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    signupMethod === 'email'
                      ? 'bg-gray-900 dark:bg-gray-700 text-white shadow-lg'
                      : 'bg-gray-100 dark:bg-gray-900 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-800'
                  }`}
                >
                  <Mail className="w-4 h-4" />
                  <span>Email</span>
                </button>
                <button
                  type="button"
                  onClick={() => setSignupMethod('phone')}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    signupMethod === 'phone'
                      ? 'bg-gray-900 dark:bg-gray-700 text-white shadow-lg'
                      : 'bg-gray-100 dark:bg-gray-900 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-800'
                  }`}
                >
                  <Phone className="w-4 h-4" />
                  <span>Phone</span>
                </button>
              </div>

              {/* Input Field */}
              <label htmlFor="contact" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {signupMethod === 'email' ? 'Email' : 'Phone'}
              </label>
              <div className="mt-1 relative bg-white dark:bg-black rounded-2xl shadow-lg overflow-hidden border-0 transition-all duration-300 hover:shadow-xl">
                {signupMethod === 'email' ? (
                  <input
                    id="contact"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="block w-full px-5 py-4 bg-transparent placeholder-gray-400 focus:outline-none focus:ring-0 sm:text-sm dark:text-white"
                    placeholder="Enter your email address"
                  />
                ) : (
                  <input
                    id="contact"
                    type="tel"
                    required
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="block w-full px-5 py-4 bg-transparent placeholder-gray-400 focus:outline-none focus:ring-0 sm:text-sm dark:text-white"
                    placeholder="Enter your phone number with country code (e.g., +1234567890)"
                  />
                )}
              </div>
            </div>

            {/* Birthday */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Birthday
              </label>
              <div className="mt-1 grid grid-cols-3 gap-3">
                <div className="relative bg-white dark:bg-black rounded-2xl shadow-lg overflow-hidden border-0 transition-all duration-300 hover:shadow-xl">
                  <select
                    value={month}
                    onChange={(e) => setMonth(e.target.value)}
                    required
                    className="block w-full px-5 py-4 bg-white dark:bg-black appearance-none focus:outline-none focus:ring-0 sm:text-sm text-gray-800 dark:text-white pr-8"
                  >
                    <option value="">Month</option>
                    {months.map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-500">
                    <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20">
                      <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" fillRule="evenodd"></path>
                    </svg>
                  </div>
                </div>
                <div className="relative bg-white dark:bg-black rounded-2xl shadow-lg overflow-hidden border-0 transition-all duration-300 hover:shadow-xl">
                  <select
                    value={day}
                    onChange={(e) => setDay(e.target.value)}
                    required
                    className="block w-full px-5 py-4 bg-white dark:bg-black appearance-none focus:outline-none focus:ring-0 sm:text-sm text-gray-800 dark:text-white pr-8"
                  >
                    <option value="">Day</option>
                    {days.map((d) => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-500">
                    <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20">
                      <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" fillRule="evenodd"></path>
                    </svg>
                  </div>
                </div>
                <div className="relative bg-white dark:bg-black rounded-2xl shadow-lg overflow-hidden border-0 transition-all duration-300 hover:shadow-xl">
                  <select
                    value={year}
                    onChange={(e) => setYear(e.target.value)}
                    required
                    className="block w-full px-5 py-4 bg-white dark:bg-black appearance-none focus:outline-none focus:ring-0 sm:text-sm text-gray-800 dark:text-white pr-8"
                  >
                    <option value="">Year</option>
                    {years.map((y) => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-500">
                    <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20">
                      <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" fillRule="evenodd"></path>
                    </svg>
                  </div>
                </div>
              </div>

            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Password
              </label>
              <div className="mt-1 relative bg-white dark:bg-black rounded-2xl shadow-lg overflow-hidden border-0 transition-all duration-300 hover:shadow-xl">
                <input
                  id="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full px-5 py-4 bg-transparent placeholder-gray-400 focus:outline-none focus:ring-0 sm:text-sm dark:text-white"
                  placeholder="Enter your password"
                />
              </div>
            </div>

            {/* Confirm Password */}
            <div>
              <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Confirm Password
              </label>
              <div className="mt-1 relative bg-white dark:bg-black rounded-2xl shadow-lg overflow-hidden border-0 transition-all duration-300 hover:shadow-xl">
                <input
                  id="confirm-password"
                  type="password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="block w-full px-5 py-4 bg-transparent placeholder-gray-400 focus:outline-none focus:ring-0 sm:text-sm dark:text-white"
                  placeholder="Confirm your password"
                />
              </div>
            </div>

            {/* Terms Agreement Checkbox */}
            <div className="mt-4">
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="terms-agreement"
                    name="terms-agreement"
                    type="checkbox"
                    required
                    checked={termsAgreed}
                    onChange={(e) => setTermsAgreed(e.target.checked)}
                    className="h-4 w-4 text-brand-green focus:ring-brand-green border-gray-300 rounded"
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="terms-agreement" className="font-medium text-gray-700 dark:text-gray-300">
                    I agree to the{' '}
                    <Link to="/creators-hub?tab=legal&legal=terms" className="text-secondary-600 dark:text-secondary-400 hover:text-secondary-700 dark:hover:text-secondary-300" onClick={() => window.scrollTo(0, 0)}>
                      Terms of Use
                    </Link>{' '}
                    and{' '}
                    <Link to="/creators-hub?tab=legal&legal=privacy" className="text-secondary-600 dark:text-secondary-400 hover:text-secondary-700 dark:hover:text-secondary-300" onClick={() => window.scrollTo(0, 0)}>
                      Privacy Policy
                    </Link>
                  </label>
                </div>
              </div>
            </div>

            {error && (
              <div className="text-sm text-red-600 dark:text-red-400">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !isUsernameAvailable || isUploadingAvatar}
              className="w-full flex justify-center py-4 px-5 border-0 rounded-2xl shadow-lg text-sm font-medium text-white bg-gray-900 dark:bg-black hover:bg-gray-800 dark:hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
            >
              {loading || isUploadingAvatar ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  {isUploadingAvatar ? 'Uploading avatar...' : 'Submitting...'}
                </>
              ) : (
                'Submit'
              )}
            </button>
          </form>
        </div>
      </div>

      <SignupSuccessModal
        isOpen={showSuccessModal}
        onClose={handleSuccessModalClose}
      />

      {/* OTP Verification Modals */}
      {showOTPModal && signupMethod === 'phone' && (
        <PhoneOTPModal
          isOpen={showOTPModal}
          onClose={() => setShowOTPModal(false)}
          onVerify={handleOTPVerify}
          loading={loading}
        />
      )}
      
      {showOTPModal && signupMethod === 'email' && (
        <EmailOTPModal
          isOpen={showOTPModal}
          onClose={() => setShowOTPModal(false)}
          onVerify={handleOTPVerify}
          loading={loading}
        />
      )}
      <LocationPermissionModal
        isOpen={showLocationPermission}
        onClose={() => setShowLocationPermission(false)}
        onAllow={handleLocationAllow}
        onDeny={handleLocationDeny}
      />
    </div>
  );
}
